import { Container, Grid, List, ListItem, TextField } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { LoadWrapper } from "..";
import { MUTATIONS, QUERIES } from "../../../graphql";
import {
  ServiceDaySlotAvailabilityOptionsModes,
  ServiceTimeSlot,
  ServiceTimeSlotReservationOptions,
  ServiceTimeSlotVariation,
} from "../../../graphql/models";
import {
  useSnackbarLazyQuery,
  useSnackbarMessage,
  useSnackbarMutation,
  useSnackbarQuery,
} from "../../../hooks";
import { dateFromUtcMinutes } from "../../../utils";

export interface ServiceTimeSlotVariationEditorProps {
  serviceId: string;
  date?: Date;
  allowChangeDate?: boolean;
}

export function ServiceTimeSlotVariationEditor(
  props: ServiceTimeSlotVariationEditorProps
) {
  const { serviceId, date: initialDate, allowChangeDate = false } = props;

  // BEWARE:
  // this is likely unneeded right now, but this might be useful someday in
  // case this editor needs to allow changing the date.
  const [date] = useState(initialDate ?? new Date());
  const weekDay = useMemo(() => date.getDay(), [date]);

  const { raiseSuccessSnackbar } = useSnackbarMessage();

  // Query to fetch the service day slot options
  const {
    data: serviceDaySlotReservationOptions,
    loading: serviceDaySlotReservationOptionsLoading,
  } = useSnackbarQuery(QUERIES.SERVICE_DAY_SLOT_RESERVATION_OPTIONS, {
    variables: {
      id: serviceId,
    },
  });

  const mode: ServiceDaySlotAvailabilityOptionsModes | null = useMemo(() => {
    if (serviceDaySlotReservationOptions) {
      return serviceDaySlotReservationOptions?.backofficeServicesFindOneById
        ?.daySlotAvailabilityOptions
        ?.mode as ServiceDaySlotAvailabilityOptionsModes;
    }

    return null;
  }, [serviceDaySlotReservationOptions]);

  // Mutation to set a variation value.
  const [
    invokeSetSlotKeyOverride,
    { loading: invokeSetSlotKeyOverrideLoading },
  ] = useSnackbarMutation(MUTATIONS.SET_SLOT_KEY_OVERRIDE, {
    onCompleted: () => {
      raiseSuccessSnackbar("valore aggiornato correttamente");
      refetchServiceTimeSlotVariations && refetchServiceTimeSlotVariations();
      // refetchServiceTimeSlotComputeKeys && refetchServiceTimeSlotComputeKeys();
    },
  });

  // Query to fetch this day's slots.
  const { data: serviceWeekDaySlotsData, loading: serviceWeekDaySlotsLoading } =
    useSnackbarQuery(QUERIES.SERVICE_WEEK_DAY_SLOTS, {
      variables: {
        serviceId,
        weekDay,
      },
    });
  const serviceTimeSlots: ServiceTimeSlot[] = useMemo(() => {
    return serviceWeekDaySlotsData?.serviceWeekDaySlots ?? [];
  }, [serviceWeekDaySlotsData]);

  // Query to fetch this day's time slot computed keys (for the above slots).
  const [
    fetchServiceTimeSlotComputeKeys,
    {
      data: serviceTimeSlotComputeKeysData,
      loading: serviceTimeSlotComputeKeysLoading,
    },
  ] = useSnackbarLazyQuery(QUERIES.SERVICE_TIME_SLOTS_COMPUTE_KEYS, {
    variables: {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      serviceId,
      slots: serviceTimeSlots,
    },
  });
  const serviceTimeSlotComputedKeys: string[] = useMemo(() => {
    return serviceTimeSlotComputeKeysData?.serviceTimeSlotsComputeKeys ?? [];
  }, [serviceTimeSlotComputeKeysData]);

  // Query to fetch the variations.
  const [
    fetchServiceTimeSlotVariations,
    {
      data: serviceTimeSlotVariationsData,
      loading: serviceTimeSlotVariationsLoading,
      refetch: refetchServiceTimeSlotVariations,
    },
  ] = useSnackbarLazyQuery(QUERIES.SERVICE_TIME_SLOT_VARIATIONS, {
    variables: {
      slotKeys: serviceTimeSlotComputedKeys,
    },
  });
  const serviceTimeSlotVariations: ServiceTimeSlotVariation[] = useMemo(() => {
    return serviceTimeSlotVariationsData?.serviceTimeSlotVariations ?? [];
  }, [serviceTimeSlotVariationsData]);

  // Once time slots are fetched, fetch the keys.
  useEffect(() => {
    if (serviceTimeSlots?.length > 0) {
      fetchServiceTimeSlotComputeKeys();
    }
  }, [serviceTimeSlots, fetchServiceTimeSlotComputeKeys]);

  // Once keys are fetched, fetch the variations.
  useEffect(() => {
    if (serviceTimeSlotComputedKeys?.length > 0) {
      fetchServiceTimeSlotVariations();
    }
  }, [serviceTimeSlotComputedKeys, fetchServiceTimeSlotVariations]);

  const findSlotVariation = (
    slotIndex: number
  ): ServiceTimeSlotVariation | undefined => {
    const slotKey = serviceTimeSlotComputedKeys[slotIndex];
    if (slotKey) {
      const variation = serviceTimeSlotVariations.find(
        (v) => v.key === slotKey
      );
      return variation;
    }

    return;
  };

  const triggerMaxUnitsChange = (slotIndex: number, newMaxUnits: number) => {
    const key = serviceTimeSlotComputedKeys[slotIndex];
    const slotRef = serviceTimeSlots[slotIndex];

    invokeSetSlotKeyOverride({
      variables: {
        slotOverrideData: {
          serviceId,
          key,
          maxUnits: newMaxUnits,
          reservationOptions: {
            ...slotRef.reservationOptions,
          },
        },
      },
    });
  };

  const triggerBookableUnitsChange = (
    slotIndex: number,
    reservationOption: ServiceTimeSlotReservationOptions,
    newAvailability: number
  ) => {
    const key = serviceTimeSlotComputedKeys[slotIndex];
    const slotRef = serviceTimeSlots[slotIndex];
    const variationRef = findSlotVariation(slotIndex);
    const { reservationOptions } = { ...(variationRef ?? slotRef) };

    const reservationOptionsCopy = [...(reservationOptions ?? [])].map(
      (opt) => {
        const { listItemCategoryName } = opt;
        let qty = opt.bookableUnits;
        if (reservationOption.listItemCategoryName === listItemCategoryName) {
          qty = newAvailability;
        }

        return {
          listItemCategoryName,
          bookableUnits: qty,
        };
      }
    );

    invokeSetSlotKeyOverride({
      variables: {
        slotOverrideData: {
          serviceId,
          key,
          maxUnits: slotRef.maxUnits,
          reservationOptions: reservationOptionsCopy,
        },
      },
    });
  };

  return (
    <LoadWrapper
      key={`${date.toLocaleString()}-${serviceId}-load-wrapper`}
      loading={
        serviceWeekDaySlotsLoading ||
        serviceTimeSlotComputeKeysLoading ||
        serviceTimeSlotVariationsLoading ||
        serviceDaySlotReservationOptionsLoading
      }
    >
      <Container>
        <Grid container spacing={1}>
          {allowChangeDate && (
            <Grid item xs={12}>
              -- datepicker here --
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            {/** List of the variations  */}
            <List>
              {serviceTimeSlots.map((serviceTimeSlot, slotIndex) => {
                const { reservationOptions } = serviceTimeSlot;
                const existingVariation = findSlotVariation(slotIndex);

                const [from, to] = [
                  dateFromUtcMinutes(
                    date,
                    serviceTimeSlot.fromTime as number
                  ).toLocaleTimeString(),
                  dateFromUtcMinutes(
                    date,
                    serviceTimeSlot.toTime as number
                  ).toLocaleTimeString(),
                ];

                if (
                  mode ===
                  ServiceDaySlotAvailabilityOptionsModes.ONE_PER_CATEGORY
                ) {
                  return (reservationOptions ?? []).map((item, idx) => {
                    let currentQuantity = item.bookableUnits;
                    let categoryVariation =
                      existingVariation?.reservationOptions?.find(
                        (v) =>
                          v.listItemCategoryName === item.listItemCategoryName
                      );

                    if (categoryVariation) {
                      currentQuantity = categoryVariation.bookableUnits;
                    }
                    return (
                      <ListItem>
                        <Grid item xs={12} md={4}>
                          <span>
                            {from} - {to}
                          </span>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            key={
                              item.listItemCategoryName +
                              "-" +
                              idx +
                              "-original"
                            }
                            disabled
                            type="number"
                            label={`${item.listItemCategoryName} predefinita`}
                            variant="outlined"
                            value={item.bookableUnits}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            key={item.listItemCategoryName + "-" + idx}
                            disabled={invokeSetSlotKeyOverrideLoading}
                            type="number"
                            label={item.listItemCategoryName}
                            variant="outlined"
                            value={currentQuantity}
                            onChange={(evt) =>
                              triggerBookableUnitsChange(
                                slotIndex,
                                item,
                                Number(evt.target.value)
                              )
                            }
                          />
                        </Grid>
                      </ListItem>
                    );
                  });
                } else {
                  return (
                    <ListItem>
                      <Grid item xs={12} md={4}>
                        <span>
                          {from} - {to}
                        </span>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          key={`slot-${slotIndex}-raw-units-text-box-default`}
                          disabled
                          type="number"
                          label={`N. professionisti predefiniti`}
                          variant="outlined"
                          value={serviceTimeSlot.maxUnits}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          key={`slot-${slotIndex}-raw-units-text-box-variation`}
                          type="number"
                          label={`N. professionisti`}
                          variant="outlined"
                          value={
                            existingVariation?.maxUnits ??
                            serviceTimeSlot.maxUnits
                          }
                          onChange={(evt) =>
                            triggerMaxUnitsChange(
                              slotIndex,
                              Number(evt.target.value)
                            )
                          }
                        />
                      </Grid>
                    </ListItem>
                  );
                }
              })}
            </List>
          </Grid>
        </Grid>
      </Container>
    </LoadWrapper>
  );
}
